import { useState } from 'react';

import { Alert } from '../../components/Alert.js';
import { PlatformIcon } from '../../components/PlatformIcon.js';
import { Tooltip } from '../../components/Tooltip.js';
import type { PlatformName } from '../../api/types.js';
import type { WidgetCatalogEntry, WidgetInstance } from '../../api/widgets.js';
import {
  CHAT_OVERLAY_PRESETS,
  DESTINATIONS,
  DESTINATION_GUIDANCE,
  WIDGET_COLOR_OPTIONS,
  type ChatOverlayPreset,
  type DestinationId,
} from './chat-overlay-options.js';
import { WidgetTypeIcon } from './WidgetTypeIcon.js';
import {
  BackdropPicker,
  WidgetPreview,
  previewState,
  syntheticMessage,
  useBackdrop,
} from './WidgetPreview.js';
import { InspectorRow, NumberField, Toggle } from './InspectorRow.js';
import { platformLabel } from '../../platforms/labels.js';

/**
 * Creating and configuring a widget (ARCHITECTURE.md §103.2).
 *
 * ==================== WHY THERE IS NO VALIDATION HERE ====================
 * There is some — enough to keep the form usable — and NONE of it is trusted.
 * The API validates the configuration against the emission table on write and
 * refuses one that could never fire (§4.2). This form's job is to make the
 * refusal unlikely, not to be the thing that prevents it.
 *
 * The platform checkboxes are drawn from `availablePlatforms`, which the
 * server computed. This file contains no per-widget capability table.
 * ========================================================================
 */

export type DraftConfig = Record<string, unknown>;

/**
 * A stored config value as form text.
 *
 * `config` is jsonb, so every field arrives as `unknown` — and `String()` on
 * an object yields "[object Object]", which would then be typed into a form
 * and saved back. Only a string or a finite number is a value a text input
 * can honestly show; anything else falls back to the default.
 */
function asText(value: unknown, fallback: string): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return fallback;
}

const DEFAULT_STYLE = {
  theme: 'minimal',
  fontScale: 1,
  align: 'left',
  textShadow: true,
  cornerRadius: 8,
  backgroundOpacity: 0,
  spacing: 8,
  animate: true,
};

const FEED_EVENTS = ['follow', 'subscription', 'membership', 'gift', 'donation', 'raid'] as const;

const EVENT_LABELS: Record<string, string> = {
  follow: 'Latest follower',
  subscription: 'Latest subscriber',
  membership: 'Latest member',
  gift: 'Latest gift',
  donation: 'Latest donation',
  raid: 'Latest raid',
};

/** The same events, named for a list of things to SHOW rather than a label. */
const FEED_EVENT_LABELS: Record<string, string> = {
  follow: 'Follows',
  subscription: 'Subscriptions',
  membership: 'Memberships',
  gift: 'Gifted subscriptions',
  donation: 'Donations, bits and Super Chats',
  raid: 'Raids',
};

export function WidgetEditor({
  entry,
  existing,
  saving,
  onCancel,
  onSave,
}: {
  entry: WidgetCatalogEntry;
  /**
   * The widget being edited, or absent when creating one.
   *
   * The SAME form for both. A separate edit form would be a second place for
   * the platform rules, the Twitch warning and the starting-value help to live
   * — and the second copy is the one that goes stale.
   */
  existing?: WidgetInstance | undefined;
  saving: boolean;
  onCancel: () => void;
  onSave: (name: string, config: DraftConfig) => void;
}): React.JSX.Element {
  const stored: Record<string, unknown> = existing?.config ?? {};
  const storedPlatforms = Array.isArray(stored.platforms)
    ? (stored.platforms as PlatformName[])
    : undefined;

  const [name, setName] = useState(existing?.name ?? entry.name);
  const [platforms, setPlatforms] = useState<PlatformName[]>(
    storedPlatforms ?? [...entry.availablePlatforms],
  );
  /*
   * Pre-acknowledged when EDITING a widget that already carries Twitch: the
   * operator accepted the rule when they created it, and re-gating an
   * unrelated change (a font size, a message limit) would be nagging rather
   * than informing. Adding Twitch to an overlay that did not have it asks
   * again, because that is a new decision.
   */
  const [backdrop, setBackdrop] = useBackdrop('grid');
  const [twitchAcknowledged, setTwitchAcknowledged] = useState(
    (storedPlatforms ?? []).includes('twitch') && (storedPlatforms ?? []).length > 1,
  );
  const [events, setEvents] = useState<string[]>(
    Array.isArray(stored.events)
      ? (stored.events as string[])
      : FEED_EVENTS.filter((event) => entry.readsEvents.includes(event)),
  );
  /*
   * Settings the schema has supported since the module shipped and the editor
   * never exposed. A configuration field with no control is a feature nobody
   * can use — and every one of these has a visible effect in the preview
   * beside it, which is the argument for surfacing them here rather than in
   * an "advanced" drawer.
   */
  const [maxMessages, setMaxMessages] = useState(Number(stored.maxMessages ?? 15));
  const [replayCount, setReplayCount] = useState(Number(stored.replayCount ?? 5));
  const [lifetime, setLifetime] = useState(Number(stored.messageLifetimeSeconds ?? 0));
  const [maxItems, setMaxItems] = useState(Number(stored.maxItems ?? 10));
  /*
   * The default differs BY TYPE, and this used to not.
   *
   * `stored.showAvatar !== false` is `true` for any new widget, so a new Chat
   * Overlay was created with avatars on — contradicting the schema, whose
   * chat default is `false`, and contradicting the Minimal Transparent intent
   * of text over gameplay rather than a column of portraits. An Activity Feed
   * genuinely does want them, which is why one expression could not serve
   * both and quietly served the wrong one.
   */
  const [showAvatar, setShowAvatar] = useState(
    typeof stored.showAvatar === 'boolean'
      ? stored.showAvatar
      : entry.type !== 'CHAT_OVERLAY',
  );
  const [showBadges, setShowBadges] = useState(stored.showBadges !== false);
  const [showTimestamp, setShowTimestamp] = useState(stored.showTimestamp === true);
  const [emotes, setEmotes] = useState(stored.emotes !== false);
  const [hideCommands, setHideCommands] = useState(stored.hideCommands === true);
  const [ignoredUsers, setIgnoredUsers] = useState(
    Array.isArray(stored.ignoredUsers) ? (stored.ignoredUsers as string[]).join(', ') : '',
  );

  const storedStyle: Record<string, unknown> =
    typeof stored.style === 'object' && stored.style !== null
      ? (stored.style as Record<string, unknown>)
      : {};

  const [fontScale, setFontScale] = useState(Number(storedStyle.fontScale ?? 1));
  const [align, setAlign] = useState(asText(storedStyle.align, 'left'));
  const [backgroundOpacity, setBackgroundOpacity] = useState(
    Number(storedStyle.backgroundOpacity ?? 0),
  );
  const [font, setFont] = useState(asText(storedStyle.font, 'system'));
  const [animation, setAnimation] = useState(asText(storedStyle.animation, 'fade'));
  const [newestAt, setNewestAt] = useState(asText(stored.newestAt, 'bottom'));
  const [textShadow, setTextShadow] = useState(storedStyle.textShadow !== false);
  const [textOutline, setTextOutline] = useState(storedStyle.textOutline === true);
  /*
   * Spacing and corner radius have been in the schema since Slice 1 with no
   * control, so every widget carried the default forever. They are here now
   * because a preset sets them and an operator who applies one then wants to
   * nudge it would otherwise be stuck with whatever the preset chose.
   */
  const [spacing, setSpacing] = useState(Number(storedStyle.spacing ?? 8));
  const [cornerRadius, setCornerRadius] = useState(Number(storedStyle.cornerRadius ?? 8));
  const [theme, setTheme] = useState(asText(storedStyle.theme, 'minimal'));
  const [animate, setAnimate] = useState(storedStyle.animate !== false);

  const [usernameColor, setUsernameColor] = useState(asText(stored.usernameColor, 'auto'));
  const [usernameColorValue, setUsernameColorValue] = useState(
    asText(stored.usernameColorValue, 'white'),
  );
  const [messageColor, setMessageColor] = useState(asText(stored.messageColor, 'white'));
  const [destination, setDestination] = useState<DestinationId>(
    asText(stored.destination, 'generic') as DestinationId,
  );

  /*
   * Preview-only, and never written to the widget.
   *
   * `injected` holds messages the operator added with the test control so they
   * can judge motion and wrapping without waiting for real chat. `paused`
   * freezes them. Neither reaches `buildConfig`, and the module they come from
   * cannot reach the network at all (§103.12).
   */
  const [injected, setInjected] = useState<Record<string, unknown>[]>([]);
  const [paused, setPaused] = useState(false);

  const [target, setTarget] = useState(asText(stored.target, '1000'));
  const [startingValue, setStartingValue] = useState(asText(stored.startingValue, '0'));
  const [currency, setCurrency] = useState(asText(stored.currency, 'USD'));
  const [label, setLabel] = useState(
    asText(stored.label, entry.readsEvents.find((event) => event !== 'chat.deleted') ?? 'follow'),
  );

  const isGoal = entry.type.endsWith('GOAL');

  /*
   * The one compliance condition Connaro cannot enforce (§23).
   *
   * Twitch prohibits combined activity ON A TWITCH BROADCAST. A browser source
   * emits pixels and OBS decides which destinations receive them, so Connaro
   * genuinely does not know — and must not pretend to. Refusing the
   * combination would be a guess in the other direction: a streamer going out
   * to YouTube and Kick while monitoring Twitch breaks nothing.
   *
   * So it is a warning the operator has to read and acknowledge, and the
   * acknowledgement is theirs rather than a checkbox Connaro pre-ticks.
   */
  /*
   * ==================== TWO AXES, AND WHY BOTH ====================
   * §23's finding is about the DESTINATION: Twitch prohibits combined
   * activity on a Twitch broadcast. Until a widget could record where the
   * operator intends to show it, the editor could only approximate that by
   * asking whether Twitch was a SOURCE — which is the wrong axis, and warns
   * the streamer going out to YouTube while monitoring Twitch about a rule
   * they are not breaking.
   *
   * Now that a destination is declared, both are used, and the gate is the
   * UNION rather than a replacement. Driving it off the declared destination
   * alone would have been a quiet weakening: "Recording" is the default, so
   * every existing merged Twitch overlay would have stopped asking. A
   * declaration is a statement of intent, not a fact Connaro can check (§23),
   * so it earns a warning rather than an exemption.
   * ================================================================
   */
  const isMerged = entry.type === 'CHAT_OVERLAY' && platforms.length > 1;
  const guidance = DESTINATION_GUIDANCE[destination];
  const twitchNeedsWarning =
    isMerged && (platforms.includes('twitch') || guidance.requiresAcknowledgement);

  /*
   * The sample lines plus whatever the operator injected, bounded by the
   * widget's own `maxMessages` so the preview obeys the setting being edited
   * rather than growing forever. `paused` freezes the list where it is.
   */
  const previewMessages = paused
    ? injected.slice(-maxMessages)
    : [
        ...(previewState('CHAT_OVERLAY').messages as Record<string, unknown>[]),
        ...injected,
      ].slice(-maxMessages);
  const isDonationGoal = entry.type === 'DONATION_GOAL';

  /**
   * Apply a preset's values to the live form.
   *
   * ==================== APPLY, NEVER RESET ====================
   * Each preset lists exactly the fields it means to set. Anything it omits
   * is left where the operator put it — so trying "Bubble" to see how it
   * looks cannot silently discard an ignore list, a platform selection or a
   * destination, none of which are visible on screen to notice going missing.
   * ============================================================
   */
  const applyPreset = (preset: ChatOverlayPreset): void => {
    const values = preset.values;
    const style = preset.style;

    if (typeof values.showAvatar === 'boolean') setShowAvatar(values.showAvatar);
    if (typeof values.showBadges === 'boolean') setShowBadges(values.showBadges);
    if (typeof values.showTimestamp === 'boolean') setShowTimestamp(values.showTimestamp);
    if (typeof values.maxMessages === 'number') setMaxMessages(values.maxMessages);
    if (typeof values.usernameColor === 'string') setUsernameColor(values.usernameColor);
    if (typeof values.messageColor === 'string') setMessageColor(values.messageColor);

    if (typeof style.theme === 'string') setTheme(style.theme);
    if (typeof style.backgroundOpacity === 'number') setBackgroundOpacity(style.backgroundOpacity);
    if (typeof style.textShadow === 'boolean') setTextShadow(style.textShadow);
    if (typeof style.textOutline === 'boolean') setTextOutline(style.textOutline);
    if (typeof style.spacing === 'number') setSpacing(style.spacing);
    if (typeof style.cornerRadius === 'number') setCornerRadius(style.cornerRadius);
    if (typeof style.fontScale === 'number') setFontScale(style.fontScale);
    if (typeof style.animation === 'string') setAnimation(style.animation);
  };

  const buildConfig = (): DraftConfig => {
    const style = {
      ...DEFAULT_STYLE,
      theme,
      spacing,
      cornerRadius,
      fontScale,
      font,
      animation,
      align,
      backgroundOpacity,
      textShadow,
      textOutline,
      animate,
    };

    if (isGoal) {
      return {
        platforms,
        name,
        startingValue: Number(startingValue) || 0,
        target: Number(target) || 1,
        ...(isDonationGoal ? { currency: currency.toUpperCase() } : {}),
        showValues: true,
        style,
      };
    }

    if (entry.type === 'CHAT_OVERLAY') {
      return {
        platforms,
        maxMessages,
        replayCount,
        messageLifetimeSeconds: lifetime,
        newestAt,
        showAvatar,
        showBadges,
        showTimestamp,
        emotes,
        usernameColor,
        usernameColorValue,
        messageColor,
        destination,
        hideCommands,
        /*
         * Split on commas, trimmed, blanks dropped. An operator types a list;
         * the schema wants an array, and it bounds both the count and each
         * name's length.
         */
        ignoredUsers: ignoredUsers
          .split(',')
          .map((entry) => entry.trim())
          .filter((entry) => entry !== '')
          .slice(0, 50),
        style,
      };
    }

    if (entry.type === 'EVENT_LABELS') {
      /*
       * A prefix by default. "Kestrel" alone on a stream says nothing; "Latest
       * follower: Kestrel" is the widget doing its job — and it matches what
       * the gallery card previews, so the first render is not a surprise.
       */
      return { platforms, label, prefix: `${EVENT_LABELS[label] ?? 'Latest'}: `, suffix: '', fallbackText: '—', style };
    }

    return {
      platforms,
      events,
      maxItems,
      showAvatar,
      showTimestamp,
      style,
    };
  };

  /*
   * The preview redraws on every keystroke, because `buildConfig()` is pure
   * and the renderer is cheap. There is no save, no request and no debounce
   * between typing a target and seeing the bar move — which is the whole
   * argument for preview-first: a value you can see is a value you can judge.
   */
  const draft = buildConfig();

  return (
    <form
      className="weditor"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(name, draft);
      }}
    >
      <header className="weditor__head">
        <h2 className="weditor__title">
          {existing === undefined ? entry.name : existing.name}
        </h2>
        <p className="weditor__sub">{entry.description}</p>
      </header>

      <div className="ipanel weditor__preview">
        <div className="ipanel__head">
          <span className="ipanel__mark" aria-hidden="true">
            <WidgetTypeIcon type={entry.type} size={18} />
          </span>
          <h3 className="ipanel__title">Browser source</h3>
          <BackdropPicker value={backdrop} onChange={setBackdrop} />
        </div>

        <div className="ipanel__body">
          <WidgetPreview
            widgetType={entry.type}
            config={draft}
            backdrop={backdrop}
            {...(entry.type === 'CHAT_OVERLAY' && injected.length > 0
              ? { state: { ...previewState(entry.type), messages: previewMessages } }
              : {})}
          />

          {entry.type === 'CHAT_OVERLAY' && (
            /*
             * Preview controls, not widget settings.
             *
             * An operator designing an overlay is usually not live, and
             * judging motion, wrapping and message limit against three static
             * lines is judging the wrong thing. Everything here is local to
             * this component — see `syntheticMessage`, which reaches nothing.
             */
            <div className="weditor__sim">
              <span className="weditor__sim-label">Test with</span>
              {platforms.map((platform) => (
                <button
                  key={platform}
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setInjected((current) => [...current, syntheticMessage(platform)]);
                  }}
                >
                  <PlatformIcon platform={platform} displayName={platform} size={14} />
                  <span>{platform}</span>
                </button>
              ))}

              <button
                type="button"
                className="btn btn--ghost btn--sm"
                aria-pressed={paused}
                onClick={() => {
                  setPaused((current) => !current);
                }}
              >
                {paused ? 'Resume' : 'Pause'}
              </button>

              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={injected.length === 0}
                onClick={() => {
                  setInjected([]);
                }}
              >
                Clear
              </button>
            </div>
          )}

          <p className="weditor__preview-note">
            Example data. Your real events replace it once this is on stream.
          </p>
          {/*
            * The URL is NOT shown here.
            *
            * Connaro hashes a browser-source token at rest, so the plaintext
            * exists once — in the response that mints it — and cannot be
            * recovered to fill this panel later. That is deliberate (§103.6)
            * and it is why rotation exists rather than a reveal. Competing
            * tools display the URL permanently; doing that would mean storing
            * it recoverably, which is a worse trade than an extra click.
            */}
          <p className="weditor__preview-note">
            {existing === undefined
              ? 'The browser source URL appears once, when you create this widget.'
              : 'Connaro cannot show this widget’s URL again. Use Rotate URL in your widget list to issue a new one.'}
          </p>
        </div>
      </div>

      <div className="ipanel weditor__inspector">
        <div className="ipanel__head">
          <span className="ipanel__mark" aria-hidden="true">
            <WidgetTypeIcon type={entry.type} size={18} />
          </span>
          <h3 className="ipanel__title">Settings</h3>
        </div>

        <div className="ipanel__body">

      <label className="field">
        <span className="field__label">Name</span>
        <input
          className="field__input"
          value={name}
          maxLength={64}
          required
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <span className="field__hint">Only you see this. It names the widget in your list.</span>
      </label>

      <fieldset className="field">
        <legend className="field__label">
          Platforms{' '}
          {entry.requiresAttribution && (
            <Tooltip tip="Every message shows which platform it came from. That is required when an overlay carries more than one platform, so a viewer is never confused about where a message originated, and it is not something Connaro lets you switch off.">
              <span className="field__why" aria-label="Why is every message labelled?">
                ?
              </span>
            </Tooltip>
          )}
        </legend>

        {entry.availablePlatforms.map((platform) => (
          <label key={platform} className="iplatform">
            <PlatformIcon platform={platform} displayName={platform} size={20} />
            <span className="iplatform__name">{platformLabel(platform)}</span>
            <Toggle
              checked={platforms.includes(platform)}
              label={platform}
              onChange={(next) => {
                setPlatforms((current) =>
                  next ? [...current, platform] : current.filter((value) => value !== platform),
                );
              }}
            />
          </label>
        ))}
      </fieldset>

      {entry.type === 'CHAT_OVERLAY' && (
        <section className="isection">
          <h4 className="isection__title">Destination</h4>

          <InspectorRow
            stacked
            label="Shown on"
            help="Where you intend to show this overlay. It is not a connection and it changes nothing about how the widget works — it only decides which guidance you see. Connaro cannot detect where your streaming software sends a scene."
          >
            <select
              className="field__input"
              value={destination}
              onChange={(event) => {
                setDestination(event.target.value as DestinationId);
              }}
            >
              {DESTINATIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </InspectorRow>

          {/*
            * Guidance appears only when the overlay is actually MERGED.
            * A Twitch-only overlay on a Twitch broadcast is the ordinary
            * case, and warning about it would train operators to click past
            * warnings — which costs more than it ever protects.
            */}
          {isMerged && !guidance.requiresAcknowledgement && (
            <p className="field__hint">{guidance.whenMerged}</p>
          )}
        </section>
      )}

      {twitchNeedsWarning && (
        <Alert tone="warning">
          <strong>Twitch does not allow merged chat on a Twitch broadcast.</strong>
          <p>{DESTINATION_GUIDANCE.twitch.whenMerged}</p>
          <p>
            Connaro cannot tell where you composite this overlay — your streaming software decides
            that — so this is yours to judge.
          </p>
          <label className="widgets__platform">
            <input
              type="checkbox"
              checked={twitchAcknowledged}
              onChange={(event) => {
                setTwitchAcknowledged(event.target.checked);
              }}
            />
            <span>
              I understand, and I will not show this overlay on a broadcast going to Twitch
            </span>
          </label>
        </Alert>
      )}

      {entry.type === 'CHAT_OVERLAY' && (
        <section className="isection">
          <h4 className="isection__title">Preset</h4>
          <p className="field__hint">
            A starting point. Applying one changes appearance only — your platforms, filters and
            destination are left alone.
          </p>

          <div className="wpresets">
            {CHAT_OVERLAY_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className="wpreset"
                onClick={() => {
                  applyPreset(preset);
                }}
              >
                <span className="wpreset__name">{preset.name}</span>
                <span className="wpreset__desc">{preset.description}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {entry.type === 'CHAT_OVERLAY' && (
        <section className="isection">
          <h4 className="isection__title">Messages</h4>

          <InspectorRow
            label="Messages on screen"
            help="How many lines the overlay keeps. Older ones drop off the top as new ones arrive."
          >
            <NumberField value={maxMessages} min={1} max={50} onChange={setMaxMessages} />
          </InspectorRow>

          <InspectorRow
            label="Show on load"
            help="How much recent chat appears when the Browser Source starts or reloads. Keep it small: a scene change should not put a backlog of old messages on stream as if they were live."
          >
            <NumberField value={replayCount} min={0} max={20} onChange={setReplayCount} />
          </InspectorRow>

          <InspectorRow
            label="Hide after"
            help="Remove a message once it reaches this age. Zero keeps it until newer messages push it off."
          >
            <NumberField value={lifetime} min={0} max={3600} unit="sec" onChange={setLifetime} />
          </InspectorRow>

          <InspectorRow label="Avatars">
            <Toggle checked={showAvatar} label="Avatars" onChange={setShowAvatar} />
          </InspectorRow>

          <InspectorRow label="Badges">
            <Toggle checked={showBadges} label="Badges" onChange={setShowBadges} />
          </InspectorRow>

          <InspectorRow
            label="Emotes"
            help="Show 7TV, BetterTTV and FrankerFaceZ emotes as images. Off means a message reads as the letters that were typed — which your viewers will notice, because they can see the emote in the platform's own chat."
          >
            <Toggle checked={emotes} label="Emotes" onChange={setEmotes} />
          </InspectorRow>

          <InspectorRow label="Timestamps">
            <Toggle checked={showTimestamp} label="Timestamps" onChange={setShowTimestamp} />
          </InspectorRow>

          <InspectorRow
            label="Hide commands"
            help="Hide messages that begin with “!” followed by a letter. Only “!” — the bot also accepts “-” and “+” as prefixes, and hiding those would swallow “+1” and “-- brb”, which people type constantly."
          >
            <Toggle checked={hideCommands} label="Hide commands" onChange={setHideCommands} />
          </InspectorRow>

          <InspectorRow
            label="New messages"
            help="Where a new line appears. An overlay pinned to the bottom of a scene grows upward, so “at the top” keeps the newest message nearest the edge it is anchored to."
          >
            <select
              className="field__input"
              value={newestAt}
              onChange={(event) => {
                setNewestAt(event.target.value);
              }}
            >
              <option value="bottom">At the bottom</option>
              <option value="top">At the top</option>
            </select>
          </InspectorRow>
          <InspectorRow
            label="Ignore"
            help="Display names never to show, separated by commas. This is also how you hide a bot: Twitch and Kick publish no bot badge, so Connaro cannot tell a bot from a person — a switch claiming to would work for whichever accounts somebody hard-coded and silently fail for yours."
          >
            <input
              className="field__input"
              value={ignoredUsers}
              placeholder="Nightbot, StreamElements"
              onChange={(event) => {
                setIgnoredUsers(event.target.value);
              }}
            />
          </InspectorRow>
        </section>
      )}

      {entry.type === 'ACTIVITY_FEED' && (
        <fieldset className="field">
          <legend className="field__label">Events to show</legend>
          {FEED_EVENTS.filter((event) => entry.readsEvents.includes(event)).map((event) => (
            <label key={event} className="widgets__platform">
              <input
                type="checkbox"
                checked={events.includes(event)}
                onChange={(changed) => {
                  setEvents((current) =>
                    changed.target.checked
                      ? [...current, event]
                      : current.filter((value) => value !== event),
                  );
                }}
              />
              <span>{FEED_EVENT_LABELS[event] ?? event}</span>
            </label>
          ))}
        </fieldset>
      )}

      {entry.type === 'ACTIVITY_FEED' && (
        <section className="isection">
          <h4 className="isection__title">Display</h4>
          <InspectorRow label="Items on screen">
            <NumberField value={maxItems} min={1} max={50} onChange={setMaxItems} />
          </InspectorRow>
          <InspectorRow label="Avatars">
            <Toggle checked={showAvatar} label="Avatars" onChange={setShowAvatar} />
          </InspectorRow>
          <InspectorRow label="Timestamps">
            <Toggle checked={showTimestamp} label="Timestamps" onChange={setShowTimestamp} />
          </InspectorRow>
        </section>
      )}

      {entry.type === 'EVENT_LABELS' && (
        <label className="field">
          <span className="field__label">Show the latest</span>
          <select
            className="field__input"
            value={label}
            onChange={(event) => {
              setLabel(event.target.value);
            }}
          >
            {FEED_EVENTS.filter((event) => entry.readsEvents.includes(event)).map((event) => (
              <option key={event} value={event}>
                {EVENT_LABELS[event] ?? event}
              </option>
            ))}
          </select>
        </label>
      )}

      {isGoal && (
        <>
          <label className="field">
            <span className="field__label">
              Starting value{' '}
              <Tooltip tip="Connaro has no follower or subscriber count from any platform. A goal shows this number plus what Connaro observes from now on, so put your current total here.">
                <span className="field__why" aria-label="What is a starting value?">
                  ?
                </span>
              </Tooltip>
            </span>
            <input
              className="field__input"
              type="number"
              min={0}
              value={startingValue}
              onChange={(event) => {
                setStartingValue(event.target.value);
              }}
            />
          </label>

          <label className="field">
            <span className="field__label">Target</span>
            <input
              className="field__input"
              type="number"
              min={1}
              value={target}
              required
              onChange={(event) => {
                setTarget(event.target.value);
              }}
            />
          </label>

          {isDonationGoal && (
            <label className="field">
              <span className="field__label">
                Currency{' '}
                <Tooltip tip="Only donations in this currency count. Connaro never converts between currencies, because it holds no exchange rate it could defend — a donation in another currency is shown in your feed and left out of this total.">
                  <span className="field__why" aria-label="Why one currency?">
                    ?
                  </span>
                </Tooltip>
              </span>
              <input
                className="field__input"
                value={currency}
                maxLength={3}
                pattern="[A-Za-z]{3}"
                required
                onChange={(event) => {
                  setCurrency(event.target.value);
                }}
              />
            </label>
          )}
        </>
      )}

      <section className="isection">
        <h4 className="isection__title">Appearance</h4>

        <InspectorRow label="Text size">
          <NumberField
            value={Math.round(fontScale * 100)}
            min={50}
            max={300}
            unit="%"
            onChange={(next) => {
              setFontScale(next / 100);
            }}
          />
        </InspectorRow>

        <InspectorRow
          label="Typeface"
          help="System typefaces only. Connaro ships no font files and its security policy forbids loading one from elsewhere, so every choice here renders instantly and needs no download mid-broadcast."
        >
          <select
            className="field__input"
            value={font}
            onChange={(event) => {
              setFont(event.target.value);
            }}
          >
            <option value="system">System default</option>
            <option value="sans">Sans</option>
            <option value="serif">Serif</option>
            <option value="mono">Monospace</option>
            <option value="wide">Wide</option>
          </select>
        </InspectorRow>

        <InspectorRow
          label="Entrance"
          help="How a new item arrives. It applies to the arriving line only, not the whole overlay, and a viewer whose system asks for reduced motion sees none of it."
        >
          <select
            className="field__input"
            value={animation}
            onChange={(event) => {
              setAnimation(event.target.value);
            }}
          >
            <option value="fade">Fade in</option>
            <option value="slide">Slide in</option>
            <option value="pop">Pop in</option>
            <option value="none">None</option>
          </select>
        </InspectorRow>

        <InspectorRow label="Align">
          <select
            className="field__input"
            value={align}
            onChange={(event) => {
              setAlign(event.target.value);
            }}
          >
            <option value="left">Left</option>
            <option value="center">Centre</option>
            <option value="right">Right</option>
          </select>
        </InspectorRow>

        <InspectorRow
          label="Background"
          help="A backing panel behind the widget, from fully transparent to solid. Most overlays want transparent; a busy scene may need some backing for the text to stay readable."
        >
          <NumberField
            value={Math.round(backgroundOpacity * 100)}
            min={0}
            max={100}
            unit="%"
            onChange={(next) => {
              setBackgroundOpacity(next / 100);
            }}
          />
        </InspectorRow>

        <InspectorRow
          label="Text shadow"
          help="Keeps text legible over bright or busy gameplay. Switch the preview to Gameplay to judge it."
        >
          <Toggle checked={textShadow} label="Text shadow" onChange={setTextShadow} />
        </InspectorRow>

        <InspectorRow
          label="Text outline"
          help="A dark edge around every letter. Solves the case a shadow cannot: white text on a white scene. Using both at once looks heavy — pick the one your scene needs."
        >
          <Toggle checked={textOutline} label="Text outline" onChange={setTextOutline} />
        </InspectorRow>

        <InspectorRow
          label="Theme"
          help="The overall treatment. Minimal has no surface of its own, Light inverts the text for a bright scene, and Neon adds a glow to names without changing your colours."
        >
          <select
            className="field__input"
            value={theme}
            onChange={(event) => {
              setTheme(event.target.value);
            }}
          >
            <option value="minimal">Minimal</option>
            <option value="dark">Dark</option>
            <option value="light">Light</option>
            <option value="neon">Neon</option>
          </select>
        </InspectorRow>

        <InspectorRow label="Line spacing">
          <NumberField value={spacing} min={0} max={48} unit="px" onChange={setSpacing} />
        </InspectorRow>

        <InspectorRow label="Corner radius">
          <NumberField
            value={cornerRadius}
            min={0}
            max={32}
            unit="px"
            onChange={setCornerRadius}
          />
        </InspectorRow>

        {entry.type === 'CHAT_OVERLAY' && (
          <>
            <InspectorRow
              label="Name colour"
              help="Automatic gives each viewer a stable colour derived from their name, so a regular looks the same all broadcast. Connaro does not receive the colour a platform assigns a user, so it cannot offer that."
            >
              <select
                className="field__input"
                value={usernameColor}
                onChange={(event) => {
                  setUsernameColor(event.target.value);
                }}
              >
                <option value="auto">Automatic, per viewer</option>
                <option value="fixed">One colour for everyone</option>
              </select>
            </InspectorRow>

            {usernameColor === 'fixed' && (
              <InspectorRow label="Name colour value">
                <select
                  className="field__input"
                  value={usernameColorValue}
                  onChange={(event) => {
                    setUsernameColorValue(event.target.value);
                  }}
                >
                  {WIDGET_COLOR_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </InspectorRow>
            )}

            <InspectorRow label="Message colour">
              <select
                className="field__input"
                value={messageColor}
                onChange={(event) => {
                  setMessageColor(event.target.value);
                }}
              >
                {WIDGET_COLOR_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </InspectorRow>
          </>
        )}

        <InspectorRow label="Animate">
          <Toggle checked={animate} label="Animate" onChange={setAnimate} />
        </InspectorRow>
      </section>
        </div>
      </div>

      <div className="weditor__actions">
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={saving || platforms.length === 0 || (twitchNeedsWarning && !twitchAcknowledged)}
        >
          {saving
            ? existing === undefined
              ? 'Creating…'
              : 'Saving…'
            : existing === undefined
              ? 'Create widget'
              : 'Save changes'}
        </button>
      </div>
    </form>
  );
}
